import React, { useEffect, useState } from "react";
import {
  TextField,
  Typography,
  Link,
  Box,
  Divider,
  Grid,
  makeStyles,
  FormControlLabel,
  Checkbox
} from "@material-ui/core";
import { Form, Field } from "react-final-form";
import { IntroText, FormActionDrawer } from "../../../components";
import createDecorator from "final-form-focus";
import { useHeader } from "../../../contexts";
import { string, object } from "yup";
import { useYupValidation } from "../../../hooks";

const focusOnErrors = createDecorator();

const useClasses = makeStyles({
  images: {
    maxWidth: "100%"
  }
});

const validationSchema = object({
  full_name: string()
    .required()
    .trim()
    .label("Full Name")
});

const ScheduleConsentProvider = ({
  setDrawerClearance,
  updateScheduleData,
  scheduleData,
  isSubmitting,
  screenFlowConfig
}) => {
  const classes = useClasses();
  const { setTitle } = useHeader();
  const [content, setContent] = useState({});

  useEffect(() => {
    setTitle("Consent");
  }, []);

  /**
   * Get content from screen flow config (returned from API)
   */
  useEffect(() => {
    if (
      screenFlowConfig["consent-provider"] &&
      screenFlowConfig["consent-provider"].content
    )
      setContent(screenFlowConfig["consent-provider"].content);
  }, [screenFlowConfig]);

  const { validate } = useYupValidation();

  const validateForm = async data => {
    const errors = await validate(validationSchema)(data);

    if (errors) return errors;

    updateScheduleData({ consent_provider: data });
  };

  return (
    <>
      {Array.isArray(content.logo) &&
      content.logo.filter(logo => logo).length > 0 ? (
        <>
          <Box textAlign="center" py={2} px={1} pb={3}>
            <Grid container alignItems="center" justify="center" spacing={4}>
              {content.logo
                .filter(logo => logo)
                .map((logo, index) => (
                  <Grid key={`logo-${index}`} item xs={6}>
                    <img src={logo} alt="" className={classes.images} />
                  </Grid>
                ))}
            </Grid>
          </Box>
          <Divider />
        </>
      ) : (
        <IntroText>{content.provider_name}</IntroText>
      )}
      <Box mt={2}>
        <Form
          onSubmit={validateForm}
          initialValues={scheduleData.consent}
          decorators={[focusOnErrors]}
          render={({ handleSubmit, submitErrors: errors = {} }) => (
            <form
              data-cy="schedule-screen-consent-provider"
              onSubmit={handleSubmit}
            >
              {content.intro_text ? (
                <Typography
                  paragraph
                  data-cy="schedule-screen-consent-intro-text"
                >
                  {content.intro_text}
                </Typography>
              ) : (
                content.provider_name && (
                  <IntroText>
                    Do you consent for us to send your results to{" "}
                    {content.provider_name}?
                  </IntroText>
                )
              )}

              <Field
                name="full_name"
                render={({ input }) => (
                  <TextField
                    {...input}
                    label="Sign your full name"
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!errors.full_name}
                    helperText={errors.full_name}
                    inputProps={{
                      "data-lpignore": "true",
                      "data-cy": "input-full_name"
                    }}
                  />
                )}
              />
              {Array.isArray(content.additional_fields) && (
                <>
                  {content.additional_fields
                    .filter(field => field.name)
                    .map(field => (
                      <React.Fragment key={`additional-field-${field.name}`}>
                        {field.type && field.type === "checkbox" ? (
                          <FormControlLabel
                            key={`additional-field-${field.name}`}
                            labelPlacement="end"
                            control={
                              <Field
                                name={field.name}
                                type="checkbox"
                                render={({ input }) => (
                                  <Checkbox
                                    {...input}
                                    required={field.required}
                                    inputProps={{
                                      "data-cy": `input-${field.name}`
                                    }}
                                    color="primary"
                                  />
                                )}
                              />
                            }
                            label={field.label}
                          />
                        ) : (
                          <Field
                            key={`additional-field-${field.name}`}
                            name={field.name}
                            render={({ input }) => (
                              <TextField
                                {...input}
                                label={field.label}
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                required={field.required}
                                error={!!errors[field.name]}
                                helperText={errors[field.name]}
                                inputProps={{
                                  "data-lpignore": "true",
                                  "data-cy": `input-${field.name}`
                                }}
                              />
                            )}
                          />
                        )}
                      </React.Fragment>
                    ))}
                </>
              )}
              <FormActionDrawer
                setDrawerClearance={setDrawerClearance}
                loading={isSubmitting}
              />
            </form>
          )}
        />
      </Box>
      {content.consent_url && (
        <Box mt={1}>
          <Typography paragraph>
            By choosing next, I acknowledge that I have reviewed and
            electronically agree to the{" "}
            <Link href={content.consent_url} target="_blank" rel="noopener">
              consent
            </Link>
            .
          </Typography>
        </Box>
      )}
    </>
  );
};

export { ScheduleConsentProvider };
